// src/pages/ConfiguracionRecordatorios.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { 
  Box, 
  Typography, 
  Paper, 
  Switch, 
  FormControlLabel, 
  Button, 
  CircularProgress, 
  Alert, 
  TextField,
  Divider,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useSubscriptionFeatures } from '../hooks/useSubscriptionFeatures';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://app-medifyhc.com';

const ConfiguracionRecordatorios = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasRecordatoriosAccess, loading: loadingFeatures } = useSubscriptionFeatures();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState({
    enviar_recordatorios: false,
    recordatorio_24h: false,
    recordatorio_3h: false,
    asunto_recordatorio: '',
    firma_correo: '',
    plantilla_recordatorio: ''
  });

  // Cargar la configuración actual
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Solo intentar cargar si el usuario tiene acceso
        if (loadingFeatures) {
          return; // Esperar a que se cargue la información de acceso
        }
        
        if (!hasRecordatoriosAccess) {
          // Si no tiene acceso, redirigir y mostrar mensaje
          toast.error('No tienes acceso a esta funcionalidad. Actualiza tu plan para acceder a recordatorios.');
          navigate('/');
          return;
        }
        
        setLoading(true);
        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('No se encontró token de autenticación');
        }
        
        const response = await axios.get(`${API_BASE_URL}/api/configuracion-recordatorios/`, {
          headers: { Authorization: `Token ${token}` }
        });
        
        setConfig(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error al cargar configuración de recordatorios:', err);
        setError('Error al cargar la configuración. Por favor, intenta de nuevo.');
        setLoading(false);
        
        // Si el error es 401 o 403, redirigir al login
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          toast.error('Sesión expirada o sin permisos. Por favor, inicia sesión nuevamente.');
          navigate('/login');
        }
      }
    };

    fetchConfig();
  }, [navigate, hasRecordatoriosAccess, loadingFeatures]);

  // Guardar la configuración
  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No se encontró token de autenticación');
      }
      
      await axios.put(`${API_BASE_URL}/api/configuracion-recordatorios/`, config, {
        headers: { Authorization: `Token ${token}` }
      });
      
      toast.success('Configuración guardada correctamente');
      setSaving(false);
    } catch (err) {
      console.error('Error al guardar configuración:', err);
      toast.error('Error al guardar la configuración');
      setSaving(false);
    }
  };

  // Manejar cambios en los campos
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value
    }));
  };

  // Si está cargando las características de suscripción, mostrar spinner
  if (loadingFeatures) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Si no tiene acceso, no renderizar nada (la redirección se maneja en el useEffect)
  if (!hasRecordatoriosAccess) {
    return null;
  }

  return (
    <Box sx={{ p: 4, maxWidth: '1000px', margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Configuración de Recordatorios
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Personaliza cómo y cuándo se envían los recordatorios de citas a tus pacientes.
      </Typography>
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: "20px"
          }}
        >
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card 
              variant="outlined" 
              sx={{ 
                mb: 3,
                borderRadius: "20px",
                boxShadow: theme.shadows[1],
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: theme.shadows[2],
                }
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Opciones Generales
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.enviar_recordatorios}
                      onChange={handleChange}
                      name="enviar_recordatorios"
                      color="primary"
                    />
                  }
                  label="Activar envío de recordatorios"
                />
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.recordatorio_24h}
                        onChange={handleChange}
                        name="recordatorio_24h"
                        color="primary"
                        disabled={!config.enviar_recordatorios}
                      />
                    }
                    label="Enviar recordatorio 24 horas antes"
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.recordatorio_3h}
                        onChange={handleChange}
                        name="recordatorio_3h"
                        color="primary"
                        disabled={!config.enviar_recordatorios}
                      />
                    }
                    label="Enviar recordatorio 3 horas antes"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card 
              variant="outlined" 
              sx={{ 
                mb: 3,
                borderRadius: "20px",
                boxShadow: theme.shadows[1],
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: theme.shadows[2],
                }
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Personalización de Correos
                </Typography>
                <TextField
                  label="Asunto del correo"
                  name="asunto_recordatorio"
                  value={config.asunto_recordatorio || ''}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  placeholder="Ej: Recordatorio de su cita médica"
                  disabled={!config.enviar_recordatorios}
                  helperText="Deja en blanco para usar el asunto predeterminado"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: '12px' 
                    } 
                  }}
                />
                <TextField
                  label="Firma del correo"
                  name="firma_correo"
                  value={config.firma_correo || ''}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  placeholder="Ej: Dr. Juan Pérez - Clínica Médica"
                  disabled={!config.enviar_recordatorios}
                  helperText="Deja en blanco para usar tu nombre como firma"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: '12px' 
                    } 
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card 
              variant="outlined"
              sx={{ 
                borderRadius: "20px",
                boxShadow: theme.shadows[1],
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: theme.shadows[2],
                }
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Plantilla de Correo Personalizada
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Puedes personalizar el contenido del correo de recordatorio. Usa las siguientes variables:
                  {'{paciente}'}, {'{medico}'}, {'{fecha_formateada}'}, {'{hora_formateada}'}, {'{motivo_cita}'}, {'{tiempo_restante}'}, {'{firma}'}
                </Typography>
                <TextField
                  label="Plantilla HTML"
                  name="plantilla_recordatorio"
                  value={config.plantilla_recordatorio || ''}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={8}
                  margin="normal"
                  placeholder="<p>Hola {paciente},</p><p>Le recordamos su cita con {medico} el día {fecha_formateada} a las {hora_formateada}.</p><p>Saludos,<br>{firma}</p>"
                  disabled={!config.enviar_recordatorios}
                  helperText="Deja en blanco para usar la plantilla predeterminada"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: '12px' 
                    } 
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate(-1)} 
          sx={{ 
            mr: 2,
            borderRadius: '20px',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}
        >
          Cancelar
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave} 
          disabled={saving || loading}
          startIcon={saving && <CircularProgress size={20} />}
          sx={{ 
            borderRadius: '20px',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}
        >
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </Button>
      </Box>
    </Box>
  );
};

export default ConfiguracionRecordatorios;
